import { useCallback, useState } from 'react'
import { useStore } from 'react-redux'
import { updateAppAlert } from '../appAlert/actions'
import { errorLogger } from '../../utils/errorLogger'
import { ChatDocumentDocType, ChatUploadedDocument } from './api'
import { presignChatDocumentAPI, uploadFileToPresignedUrl } from './documentApi'
import { useLabraChatDispatch } from './context'
import { LabraChatThunkState } from './actions'
import { useTrackEvent } from '../../telemetry/analytics'

/**
 * Best-effort guess of a funding document's type from its filename. The agent
 * cares most about the SOW and the pricing calculator; anything else is left
 * as `other` rather than mislabelled.
 */
export const inferDocType = (filename: string): ChatDocumentDocType => {
  const lower = filename.toLowerCase()
  if (lower.includes('calculator') || lower.includes('pricing')) {
    return 'pricing_calculator'
  }
  if (lower.includes('sow') || lower.includes('statement of work')) {
    return 'sow'
  }
  return 'other'
}

/**
 * Uploads a funding document to S3 via the chat-document presign flow and
 * returns the `ChatUploadedDocument` the chat stream expects. Presign → bare
 * PUT to S3 → `{ filename, s3_uri, doc_type }`. Surfaces an alert and returns
 * `null` on failure so callers can simply skip a doc that didn't upload.
 */
export const useChatDocumentUpload = (partnerId: string) => {
  const dispatch = useLabraChatDispatch()
  const store = useStore<LabraChatThunkState>()
  const track = useTrackEvent()
  const [isUploading, setIsUploading] = useState(false)

  const uploadDocument = useCallback(
    async (file: File): Promise<ChatUploadedDocument | null> => {
      if (!partnerId) return null
      setIsUploading(true)
      try {
        const docType = inferDocType(file.name)
        const contentType = file.type || 'application/pdf'
        const { data } = await presignChatDocumentAPI(partnerId, {
          filename: file.name,
          doc_type: docType,
          content_type: contentType,
        })
        await uploadFileToPresignedUrl(data.upload_url, file, data.content_type)
        track({
          name: 'labra_ai_chat_attachment_uploaded',
          conversation_id: store.getState().labraChat?.sessionId || undefined,
          filename: file.name,
        })
        return { filename: file.name, s3_uri: data.s3_uri, doc_type: docType }
      } catch (error) {
        dispatch(
          updateAppAlert({
            message: 'Unable to upload document. Please try again.',
            messageType: 'ERROR',
            autoClose: true,
          })
        )
        errorLogger({ globalState: store.getState() })(error as Error)
        return null
      } finally {
        setIsUploading(false)
      }
    },
    [partnerId, dispatch, store, track]
  )

  return { uploadDocument, isUploading }
}
